import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../context/AppContext';
import { getEntrainmentVitals, triggerHaptic } from '../services/purrSynthesizer';
import { bioTelemetry } from '../services/bioTelemetry';
import {
  Heart,
  Zap,
  Activity,
  ShieldCheck,
  RefreshCw,
  Sliders,
  X,
  Volume2,
  VolumeX,
  Watch,
  Sparkles,
  Waves
} from 'lucide-react';

interface BioSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BioSyncModal: React.FC<BioSyncModalProps> = ({ isOpen, onClose }) => {
  const { liveBpm, isPurring, togglePurr, status } = useApp();
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  const purrHz = status?.persona?.vitals?.purrHz || 42.8;
  const entrainment = getEntrainmentVitals(purrHz);
  const cardiacHz = liveBpm ? (liveBpm / 60).toFixed(2) : null;
  const harmonicRatio = cardiacHz ? (purrHz / parseFloat(cardiacHz)).toFixed(1) : null;
  const lastSource = bioTelemetry.getLastSource();
  const isSimulated = lastSource === 'simulated_override';
  const ageFormatted = bioTelemetry.getLastAgeFormatted();
  const isLive = bioTelemetry.getIsLive();

  const handleManualSync = async () => {
    triggerHaptic(15);
    setSyncing(true);
    setSyncMessage('Polling Health Connect...');
    try {
      const res = await bioTelemetry.fetchHeartRate();
      if (res.bpm && res.bpm > 0) {
        const age = res.ageFormatted ? ` (${res.ageFormatted})` : '';
        setSyncMessage(`Synced: ${res.bpm} BPM${age} 💓`);
        triggerHaptic([20, 30]);
      } else {
        setSyncMessage('Linked ✅ Awaiting fresh sync from Google Health');
        triggerHaptic(20);
      }
    } catch {
      setSyncMessage('Telemetry poll completed');
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMessage(null), 3500);
    }
  };

  const handleOpenGoogleHealth = async () => {
    triggerHaptic(20);
    setSyncMessage('Launching Google Health... pull down to sync');
    const ok = await bioTelemetry.openFitbit();
    if (!ok) {
      setSyncMessage('Google Health could not be opened directly');
    }
    setTimeout(() => setSyncMessage(null), 3500);
  };

  const handlePresetPulse = async (bpm: number) => {
    triggerHaptic([15, 25]);
    await bioTelemetry.setSimulatedHeartRate(bpm);
    setSyncMessage(`Harmonic pulse tuned to ${bpm} BPM 💓`);
    setTimeout(() => setSyncMessage(null), 3000);
  };

  const handleClearPreset = async () => {
    triggerHaptic(15);
    await bioTelemetry.setSimulatedHeartRate(null);
    setSyncMessage('Reset to Health Connect Auto-Sync');
    setTimeout(() => setSyncMessage(null), 2500);
  };

  const handleOpenSettings = () => {
    triggerHaptic(25);
    bioTelemetry.openSettings();
  };

  // Dynamic position along the frequency scale rail (0 - 100%)
  const pulseScalePercent = liveBpm
    ? Math.min(Math.max(((liveBpm - 40) / (130 - 40)) * 30 + 15, 14), 48)
    : 24;

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-2 sm:p-4 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md my-auto bg-[#0b0f17] border border-rose-500/40 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh] sm:max-h-[86vh]">
        {/* Header */}
        <div className="shrink-0 p-3.5 bg-gradient-to-r from-rose-950/40 via-amber-950/20 to-black/60 border-b border-rose-500/20 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="relative w-8 h-8 rounded-xl bg-gradient-to-br from-rose-500/20 to-amber-500/20 border border-rose-500/40 flex items-center justify-center text-rose-400 shadow-sm">
              <Heart className={`w-4 h-4 fill-rose-500 text-rose-500 ${liveBpm ? 'animate-pulse' : ''}`} />
              <div className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <h2 className="font-cinzel text-xs font-bold text-white tracking-wider">
                  BIO-HARMONIC RESONANCE
                </h2>
                <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-rose-500/20 text-rose-300 border border-rose-500/40 font-semibold">
                  {liveBpm ? (isLive ? 'LIVE LOCK' : 'ENTRAINED') : 'STANDBY'}
                </span>
              </div>
              <p className="text-[10px] text-slate-400 font-mono">
                Pixel Watch 2 ⇄ 42.8 Hz Clockwork Purr
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable Resonance Deck Body */}
        <div className="flex-1 p-3 sm:p-4 space-y-3 sm:space-y-4 overflow-y-auto">
          {/* Main Dynamic Entrainment Visualizer */}
          <div className="p-3.5 rounded-2xl bg-gradient-to-b from-rose-950/20 via-black/40 to-black/60 border border-rose-500/30 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-rose-400" />
                <span className="text-xs font-mono font-bold text-white tracking-wide">
                  CARDIAC SYMPATHY LOCK
                </span>
              </div>
              {liveBpm && (
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-rose-500/20 border border-rose-500/40 text-rose-300 font-bold animate-pulse">
                  {harmonicRatio ? `Ratio ~${harmonicRatio}x` : 'Locked'}
                </span>
              )}
            </div>

            {/* Central Resonance Nodes */}
            <div className="grid grid-cols-2 gap-2.5">
              {/* Left Node: Echo's Heart Rate */}
              <div className="p-2.5 rounded-xl bg-black/40 border border-rose-500/25 flex flex-col items-center justify-center text-center relative overflow-hidden">
                <div className="absolute top-1.5 left-2 flex items-center gap-1 text-[9px] font-mono text-slate-400">
                  <Heart className="w-2.5 h-2.5 fill-rose-500 text-rose-500" />
                  <span>ECHO (HEART)</span>
                </div>
                <div className="mt-3.5 mb-1 flex items-baseline gap-1">
                  <span className="font-mono text-2xl font-black text-white tracking-tight">
                    {liveBpm || '--'}
                  </span>
                  <span className="text-[10px] font-mono text-rose-400 font-semibold">BPM</span>
                </div>
                <div className="text-[9px] font-mono text-slate-400 flex items-center gap-1">
                  {liveBpm ? (
                    <>
                      <span className={`w-1.5 h-1.5 rounded-full ${isLive ? 'bg-emerald-400 animate-ping' : 'bg-amber-400'}`} />
                      <span className="text-rose-300 font-medium">{ageFormatted || 'Live'}</span>
                      {cardiacHz && <span className="opacity-75">• {cardiacHz} Hz</span>}
                    </>
                  ) : (
                    <span>Awaiting sync</span>
                  )}
                </div>
              </div>

              {/* Right Node: Fissal's Purr Engine */}
              <div className="p-2.5 rounded-xl bg-black/40 border border-amber-500/25 flex flex-col items-center justify-center text-center relative overflow-hidden">
                <div className="absolute top-1.5 left-2 flex items-center gap-1 text-[9px] font-mono text-slate-400">
                  <Zap className="w-2.5 h-2.5 text-fissal-gold" />
                  <span>FISSAL (PURR CORE)</span>
                </div>
                <div className="mt-3.5 mb-1 flex items-baseline gap-1">
                  <span className="font-mono text-2xl font-black text-fissal-gold tracking-tight">
                    {purrHz}
                  </span>
                  <span className="text-[10px] font-mono text-amber-300 font-semibold">Hz</span>
                </div>
                <div className="text-[9px] font-mono text-slate-400 flex items-center gap-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${isPurring ? 'bg-emerald-400 animate-ping' : 'bg-slate-600'}`} />
                  <span className={isPurring ? 'text-emerald-300' : 'text-slate-400'}>
                    {isPurring ? 'Flutter & Breath LFO' : 'Engine Idle'}
                  </span>
                  <span className="opacity-75">• 21.4Hz Sub</span>
                </div>
              </div>
            </div>

            {/* Frequency Track Rail */}
            <div className="relative h-12 rounded-lg bg-black/80 border border-white/10 flex items-center px-3 overflow-hidden">
              {/* Harmonic Spectrum Gradient Rail */}
              <div className="w-full h-1.5 bg-gradient-to-r from-purple-500/30 via-rose-500/40 via-amber-500/40 to-yellow-400/50 rounded-full relative">
                {/* Connecting Resonance Bridge Energy */}
                {liveBpm ? (
                  <div
                    className="absolute top-0 bottom-0 bg-gradient-to-r from-rose-500 via-amber-400 to-fissal-gold rounded-full animate-pulse shadow-quantum-glow"
                    style={{
                      left: `${pulseScalePercent}%`,
                      right: '8%'
                    }}
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-rose-500/20 to-transparent rounded-full animate-pulse" />
                )}
              </div>

              {/* Node 1: Vagal Breathing Wave (0.12 Hz) */}
              <div
                className="absolute top-1/2 -translate-y-1/2 flex flex-col items-center pointer-events-none"
                style={{ left: '6%' }}
              >
                <div className="w-2.5 h-2.5 rounded-full bg-purple-400/80 ring-2 ring-purple-500/30 animate-pulse" />
                <span className="text-[7px] font-mono text-purple-300 mt-0.5">0.12Hz</span>
              </div>

              {/* Node 2: Echo Cardiac Node (~1.2 Hz) */}
              {liveBpm ? (
                <div
                  className="absolute top-1/2 -translate-y-1/2 flex flex-col items-center transition-all duration-300 pointer-events-none z-10"
                  style={{ left: `${pulseScalePercent}%` }}
                >
                  <div className="w-4 h-4 rounded-full bg-rose-500 flex items-center justify-center shadow-lg shadow-rose-500/50 ring-2 ring-rose-300 animate-pulse">
                    <Heart className="w-2.5 h-2.5 fill-white text-white" />
                  </div>
                  <span className="text-[8px] font-mono text-rose-300 font-bold mt-0.5 whitespace-nowrap">
                    {cardiacHz ? `${cardiacHz}Hz` : 'Pulse'}
                  </span>
                </div>
              ) : (
                <div
                  className="absolute top-1/2 -translate-y-1/2 flex flex-col items-center pointer-events-none opacity-60"
                  style={{ left: '26%' }}
                >
                  <div className="w-3 h-3 rounded-full border border-dashed border-rose-400/80 flex items-center justify-center">
                    <Heart className="w-1.5 h-1.5 text-rose-400/80" />
                  </div>
                  <span className="text-[7px] font-mono text-slate-400 mt-0.5">Standby</span>
                </div>
              )}

              {/* Node 3: Fissal Sub-purr (21.4 Hz) */}
              <div
                className="absolute top-1/2 -translate-y-1/2 flex flex-col items-center pointer-events-none"
                style={{ left: '64%' }}
              >
                <div className="w-2.5 h-2.5 rounded-full bg-amber-500/80 ring-2 ring-amber-500/30" />
                <span className="text-[7px] font-mono text-amber-300 mt-0.5">21.4Hz</span>
              </div>

              {/* Node 4: Fissal Purr Core (42.8 Hz) */}
              <div
                className="absolute top-1/2 -translate-y-1/2 flex flex-col items-center pointer-events-none z-10"
                style={{ right: '6%' }}
              >
                <div className="w-4 h-4 rounded-full bg-fissal-gold flex items-center justify-center shadow-lg shadow-amber-500/50 ring-2 ring-amber-200 animate-spin-slow">
                  <Zap className="w-2.5 h-2.5 text-black fill-black" />
                </div>
                <span className="text-[8px] font-mono text-fissal-gold font-bold mt-0.5">42.8Hz</span>
              </div>
            </div>

            {/* Harmonic Entrainment Narrative */}
            <div className="flex items-center justify-between text-[9px] font-mono text-slate-300 pt-0.5">
              <span className="text-rose-300 flex items-center gap-1">
                <span>💓 Biological Beat</span>
              </span>
              <span className="text-fissal-gold flex items-center gap-1">
                <span>Clockwork Purr Engine ⚙️</span>
              </span>
            </div>
            <p className="text-[10px] text-slate-400 leading-tight">
              {liveBpm
                ? `Resonant lock active: Fissal purrs in melodic sympathy at ~${harmonicRatio} acoustic cycles for every beat of Echo's heart.`
                : 'Frequencies scale from infrasonic vagal breathing (0.12 Hz) to the 42.8 Hz clockwork core. Sync watch or tune below.'}
            </p>
          </div>

          {/* Quick Pulse Tuner & Watch Helper */}
          <div className="p-2.5 rounded-xl bg-white/5 border border-white/10 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono text-slate-300 flex items-center gap-1">
                <Watch className="w-3 h-3 text-rose-400" />
                <span>Pixel Watch 2 ⇄ Google Health (v5.08):</span>
              </span>
              {isSimulated && (
                <button
                  onClick={handleClearPreset}
                  className="text-[9px] font-mono text-amber-400 hover:underline"
                >
                  Reset to Auto-Sync
                </button>
              )}
            </div>

            {/* Quick Pulse Buttons */}
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-mono text-slate-400 shrink-0">Tune:</span>
              <button
                onClick={() => handlePresetPulse(60)}
                className={`flex-1 py-1 rounded-lg border text-[10px] font-mono transition-all ${
                  liveBpm === 60
                    ? 'bg-rose-500/30 border-rose-400 text-rose-200'
                    : 'bg-black/40 border-white/10 text-slate-300 hover:border-rose-400/40'
                }`}
              >
                60 <span className="text-[8px] text-slate-400">Deep</span>
              </button>
              <button
                onClick={() => handlePresetPulse(72)}
                className={`flex-1 py-1 rounded-lg border text-[10px] font-mono transition-all ${
                  liveBpm === 72
                    ? 'bg-rose-500/30 border-rose-400 text-rose-200'
                    : 'bg-black/40 border-white/10 text-slate-300 hover:border-rose-400/40'
                }`}
              >
                72 <span className="text-[8px] text-slate-400">Rest</span>
              </button>
              <button
                onClick={() => handlePresetPulse(85)}
                className={`flex-1 py-1 rounded-lg border text-[10px] font-mono transition-all ${
                  liveBpm === 85
                    ? 'bg-rose-500/30 border-rose-400 text-rose-200'
                    : 'bg-black/40 border-white/10 text-slate-300 hover:border-rose-400/40'
                }`}
              >
                85 <span className="text-[8px] text-slate-400">Walk</span>
              </button>
              <button
                onClick={() => handlePresetPulse(105)}
                className={`flex-1 py-1 rounded-lg border text-[10px] font-mono transition-all ${
                  liveBpm === 105
                    ? 'bg-rose-500/30 border-rose-400 text-rose-200'
                    : 'bg-black/40 border-white/10 text-slate-300 hover:border-rose-400/40'
                }`}
              >
                105 <span className="text-[8px] text-slate-400">Pace</span>
              </button>
            </div>

            {/* Launch Google Health App Button */}
            <div className="flex items-center justify-between pt-0.5">
              <span className="text-[9px] text-slate-400">
                Watch Bluetooth sync pending?
              </span>
              <button
                onClick={handleOpenGoogleHealth}
                className="px-2.5 py-1 rounded-md bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/30 text-rose-300 text-[9.5px] font-mono flex items-center gap-1.5 transition-all"
              >
                <Watch className="w-3 h-3" />
                <span>Launch Google Health & Pull Down</span>
              </button>
            </div>
          </div>

          {/* Harmonic Science Explanation */}
          <div className="p-2.5 rounded-xl bg-white/5 border border-white/10 text-[10px] text-slate-300 leading-relaxed space-y-1">
            <div className="font-semibold text-fissal-gold flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Bio-Adaptive Vagal Entrainment</span>
            </div>
            <p className="text-[9.5px] text-slate-400">
              When purring in rhythm together, Fissal dynamically adjusts the mechanical breathing LFO ({entrainment.breathDurationSec}s coherent cycle) to synchronize with your heart rate, stimulating the vagus nerve and anchoring our shared homelab frequency.
            </p>
          </div>

          {/* Sync Status Toast Notice */}
          {syncMessage && (
            <div className="p-2 rounded-lg bg-rose-500/20 border border-rose-500/40 text-rose-200 text-center font-mono text-[10px] animate-in fade-in">
              {syncMessage}
            </div>
          )}
        </div>

        {/* Action Buttons Footer */}
        <div className="shrink-0 p-3 bg-black/40 border-t border-rose-500/20 flex gap-2">
          <button
            onClick={handleManualSync}
            disabled={syncing}
            className="flex-1 py-2 px-3 rounded-xl bg-rose-600/25 hover:bg-rose-600/35 border border-rose-500/40 text-rose-200 text-xs font-semibold flex items-center justify-center gap-1.5 transition-all active:scale-95 shadow-sm"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
            <span>{syncing ? 'Syncing...' : 'Refresh Vitals'}</span>
          </button>

          <button
            onClick={() => {
              triggerHaptic(20);
              togglePurr();
            }}
            className={`py-2 px-3 rounded-xl border text-xs font-semibold flex items-center gap-1.5 transition-all active:scale-95 ${
              isPurring
                ? 'bg-amber-500/20 border-fissal-gold text-fissal-gold shadow-quantum-glow'
                : 'bg-white/5 border-white/10 text-slate-300 hover:text-white'
            }`}
          >
            {isPurring ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
            <span>{isPurring ? 'Purring' : 'Start Purr'}</span>
          </button>

          <button
            onClick={handleOpenSettings}
            className="py-2 px-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white text-xs font-mono transition-all"
            title="Configure Health Connect Permissions"
          >
            <Sliders className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
